let context: AudioContext | null = null;
let voiceBuffer: AudioBuffer | null = null;
let voiceLoading: Promise<AudioBuffer | null> | null = null;
let generation = 0;
let cueTimeout: ReturnType<typeof setTimeout> | null = null;
const sources = new Set<AudioScheduledSourceNode>();
export const REST_VOICE_PHRASE = 'Time to exercise';
export const REST_VOICE_URL = '/audio/time-to-exercise.wav';
export type RestAlertMode = 'voice' | 'soft_chime' | 'off';

/** Never request exclusive playback focus. Ducking strength is controlled by the OS. */
function setAudioSession(type: 'ambient' | 'transient') {
  try {
    const session=(navigator as Navigator & {audioSession?:{type:string}}).audioSession;
    if(!session) return;
    try {session.type=type;} catch {if(type==='transient') session.type='ambient';}
  } catch {/* Older browsers may expose an unsupported or read-only implementation. */}
}

function restoreAmbient() {
  if(cueTimeout!==null) clearTimeout(cueTimeout);
  cueTimeout=null;
  setAudioSession('ambient');
}

function beginCue(token:number) {
  setAudioSession('transient');
  // Bounded even if Safari suspends the context or misses onended.
  cueTimeout=setTimeout(()=>{if(token===generation)cancelRestAlert();},4000);
}

function audioContext(): AudioContext | null {
  setAudioSession('ambient');
  const Constructor=window.AudioContext??(window as Window & {webkitAudioContext?:typeof AudioContext}).webkitAudioContext;
  if(!Constructor) return null;
  if(!context||context.state==='closed') {context=new Constructor();voiceBuffer=null;voiceLoading=null;}
  return context;
}

async function before<T>(promise:Promise<T>,deadline:number):Promise<T|null> {
  return new Promise(resolve=>{
    const timer=window.setTimeout(()=>resolve(null),Math.max(0,deadline-Date.now()));
    void promise.then(value=>{clearTimeout(timer);resolve(value);},()=>{clearTimeout(timer);resolve(null);});
  });
}

function loadVoice(ctx:AudioContext):Promise<AudioBuffer|null> {
  if(voiceBuffer) return Promise.resolve(voiceBuffer);
  if(voiceLoading) return voiceLoading;
  const controller=new AbortController();
  const pending=before(fetch(REST_VOICE_URL,{cache:'force-cache',signal:controller.signal}).then(response=>{
    if(!response.ok) throw new Error('Voice clip unavailable');
    return response.arrayBuffer();
  }).then(bytes=>ctx.decodeAudioData(bytes)),Date.now()+6000).then(buffer=>{
    if(buffer&&context===ctx) voiceBuffer=buffer;
    return buffer;
  }).finally(()=>{
    controller.abort();
    if(voiceLoading===pending&&!voiceBuffer) voiceLoading=null;
  });
  voiceLoading=pending;
  return pending;
}

function track(source:AudioScheduledSourceNode) {
  sources.add(source);
  const token=generation;
  source.onended=()=>{
    sources.delete(source);
    try {source.disconnect();} catch {/* already disconnected */}
    if(token===generation&&sources.size===0)restoreAmbient();
  };
}

/** Cancel queued/playing cues and release OS ducking when moving on. */
export function cancelRestAlert() {
  generation+=1;
  for(const source of sources) {try {source.stop();source.disconnect();} catch {/* already stopped */}}
  sources.clear();
  restoreAmbient();
}

/** Invoke synchronously from the Done/Preview gesture before any awaits. */
export async function primeRestAlert(mode:RestAlertMode):Promise<void> {
  cancelRestAlert();
  if(mode==='off') return;
  try {
  const ctx=audioContext();
  if(!ctx) return;
  // Calling resume here, not after fetch/decode, preserves the iOS gesture.
  const resumed=ctx.state==='running'?Promise.resolve():ctx.resume();
  const decoded=mode==='voice'?loadVoice(ctx):Promise.resolve(null);
  await before(Promise.all([resumed,decoded]),Date.now()+2500);
  } catch {/* Priming must never block saving a set or switch to system speech. */}
}

export async function primeChime():Promise<void> {await primeRestAlert('soft_chime');}

function chime(ctx:AudioContext,compact=false) {
  const notes=compact?[[523.25,0]] as const:[[523.25,0],[659.25,0.7],[783.99,1.4]] as const;
  for(const [frequency,offset] of notes) {
    const oscillator=ctx.createOscillator();const gain=ctx.createGain();
    const now=ctx.currentTime+offset,duration=compact?0.25:1.25;
    oscillator.type='sine';oscillator.frequency.value=frequency;
    gain.gain.setValueAtTime(0.0001,now);
    gain.gain.exponentialRampToValueAtTime(compact?0.045:0.11,now+0.04);
    gain.gain.exponentialRampToValueAtTime(0.0001,now+duration);
    oscillator.connect(gain).connect(ctx.destination);track(oscillator);
    oscillator.start(now);oscillator.stop(now+duration+0.05);
  }
}

export async function playGentleChime():Promise<void> {await playRestAlert('soft_chime');}

export async function playRestAlert(mode:RestAlertMode,latestStartAt=Date.now()+4000):Promise<RestAlertMode> {
  cancelRestAlert();
  if(mode==='off'||Date.now()>latestStartAt) return 'off';
  const token=generation;
  try {
  const ctx=audioContext();
  if(ctx) {
    // Safari may leave a context interrupted after another app's audio. Retry
    // resume once within this cue's deadline instead of silently giving up.
    let resumed=ctx.state==='running'?true:await before(ctx.resume().then(()=>true),Math.min(latestStartAt,Date.now()+1500));
    if(token===generation && Date.now()<latestStartAt && ctx.state!=='running')
      resumed=await before(ctx.resume().then(()=>true),Math.min(latestStartAt,Date.now()+1000));
    if(token!==generation||Date.now()>latestStartAt) return 'off';
    if(resumed&&ctx.state==='running') {
      if(mode==='soft_chime') {beginCue(token);chime(ctx);return 'soft_chime';}
      const buffer=await before(loadVoice(ctx),Math.min(latestStartAt,Date.now()+1500));
      if(token!==generation||Date.now()>latestStartAt) return 'off';
      if(buffer) {
        // The fixed phrase uses the same unlocked audio path as its gentle cue.
        // It is independent of asynchronously installed/paused system TTS voices.
        beginCue(token);chime(ctx,true);
        const source=ctx.createBufferSource();source.buffer=buffer;
        source.connect(ctx.destination);track(source);
        source.start(ctx.currentTime+0.3);
        source.stop(ctx.currentTime+0.3+Math.min(buffer.duration,3));
        return 'voice';
      }
      beginCue(token);chime(ctx,true);return 'soft_chime';
    }
  }
  return 'off';
  } catch {
    if(token===generation)cancelRestAlert();
    return 'off';
  }
}
